import React, { useState } from 'react';
import { Box, Table, Thead, Tbody, Tr, Th, Td, Button, IconButton, Flex, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, useDisclosure, FormControl, FormLabel, Input, Grid, Select } from '@chakra-ui/react';
import { EditIcon, DeleteIcon, AddIcon } from '@chakra-ui/icons';

const Staff = () => {
  // Updated initial state to include phone number
  const [staffList, setStaffList] = useState([
    { id: 1, name: 'Alice Johnson', role: 'Manager', email: 'alice@example.com', phone: '111-111-1111', access_level: 'High' },
    { id: 2, name: 'Bob Smith', role: 'Assistant', email: 'bob@example.com', phone: '222-222-2222', access_level: 'Low' }
  ]);
  
  // New state for search and filter
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isOpenDelete, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure();
  const [deletingStaff, setDeletingStaff] = useState(null);
  const [editingStaff, setEditingStaff] = useState(null);
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newAccessLevel, setNewAccessLevel] = useState('');

  const handleAdd = () => {
    setEditingStaff(null);
    setNewName('');
    setNewRole('');
    setNewEmail('');
    setNewPhone('');
    setNewAccessLevel('');
    onOpen();
  };

  const handleEdit = (staff) => {
    setEditingStaff(staff);
    setNewName(staff.name);
    setNewRole(staff.role);
    setNewEmail(staff.email);
    setNewPhone(staff.phone);
    setNewAccessLevel(staff.access_level || '');
    onOpen();
  };

  const handleSave = () => {
    if (editingStaff) {
      const updatedList = staffList.map(staff =>
        staff.id === editingStaff.id
          ? { ...staff, name: newName, role: newRole, email: newEmail, phone: newPhone, access_level: newAccessLevel }
          : staff
      );
      setStaffList(updatedList);
    } else {
      const newStaff = { id: staffList.length + 1, name: newName, role: newRole, email: newEmail, phone: newPhone, access_level: newAccessLevel };
      setStaffList([...staffList, newStaff]);
    }
    setEditingStaff(null);
    onClose();
  };

  const handleDeleteClick = (staff) => {
    setDeletingStaff(staff);
    onOpenDelete();
  };

  const confirmDelete = () => {
    setStaffList(staffList.filter(s => s.id !== deletingStaff.id));
    setDeletingStaff(null);
    onCloseDelete();
  };

  // Filter staff by search term and position; case-insensitive matching on name/email.
  const filteredStaff = staffList.filter(staff => {
    const searchMatch =
      staff.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      staff.email.toLowerCase().includes(searchTerm.toLowerCase());
    const roleMatch = filterRole ? staff.role === filterRole : true;
    return searchMatch && roleMatch;
  });

  return (
    <Box p={4}>
      {/* Header */}
      <Flex align="center" justify="space-between" mb={4}>
        <Box fontSize="xl" fontWeight="bold">Staff List</Box>
        <Button leftIcon={<AddIcon />} colorScheme="teal" onClick={handleAdd}>
          Add Staff
        </Button>
      </Flex>

      {/* Search and Filter */}
      <Flex mb={4} gap={4}>
        <FormControl maxW="300px">
          <FormLabel mb={1}>Search</FormLabel>
          <Input 
            placeholder="Search by name or email" 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
          />
        </FormControl>
        <FormControl maxW="200px">
          <FormLabel mb={1}>Filter by Role</FormLabel>
          <Select 
            placeholder="All"
            value={filterRole} 
            onChange={(e) => setFilterRole(e.target.value)}
          >
            {/* Predefined positions; add more if needed */}
            <option value="Manager">Manager</option>
            <option value="Assistant">Assistant</option>
            <option value="Admin">Admin</option>
          </Select>
        </FormControl>
      </Flex>

      {/* Staff Table */}
      <Table variant="outline" sx={{ td: { border: '1px solid', borderColor: 'gray.200' }, th: { border: '1px solid', borderColor: 'gray.200' } }}>
        <Thead>
          <Tr>
            <Th>ID</Th>
            <Th>Name</Th>
            <Th>Role</Th>
            <Th>Access Level</Th>
            <Th>Email</Th>
            <Th>Phone</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {filteredStaff.map(staff => (
            <Tr key={staff.id}>
              <Td>{staff.id}</Td>
              <Td>{staff.name}</Td>
              <Td>{staff.role}</Td>
              <Td>{staff.access_level}</Td>
              <Td>{staff.email}</Td>
              <Td>{staff.phone}</Td>
              <Td>
                <IconButton aria-label="Edit Staff" icon={<EditIcon />} size="sm" mr={2} onClick={() => handleEdit(staff)} />
                <IconButton aria-label="Delete Staff" icon={<DeleteIcon />} size="sm" onClick={() => handleDeleteClick(staff)} />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {/* Add/Edit Staff Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent size="5xl">
          <ModalHeader textAlign="center">{editingStaff ? "Edit Staff" : "Add Staff"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Grid templateColumns="repeat(2, 1fr)" gap={6}>
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Enter name" />
              </FormControl>
              <FormControl>
                <FormLabel>Role</FormLabel>
                <Input value={newRole} onChange={(e) => setNewRole(e.target.value)} placeholder="Enter role" />
              </FormControl>
              <FormControl>
                <FormLabel>Access Level</FormLabel>
                <Input value={newAccessLevel} onChange={(e) => setNewAccessLevel(e.target.value)} placeholder="e.g. Low / Medium / High" />
              </FormControl>
            </Grid>
            <FormControl mt={6}>
              <FormLabel>Email</FormLabel>
              <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="Enter email" />
            </FormControl>
            <FormControl mt={6}>
              <FormLabel>Phone</FormLabel>
              <Input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="Enter phone number" />
            </FormControl>
          </ModalBody>
          <ModalFooter justifyContent="center">
            <Button colorScheme="teal" mx={3} onClick={handleSave}>
              Save
            </Button>
            <Button variant="ghost" mx={3} onClick={onClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isOpenDelete} onClose={() => { onCloseDelete(); setDeletingStaff(null); }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textAlign="center">Confirm Delete</ModalHeader>
          <ModalCloseButton />
          <ModalBody textAlign="center">
            {deletingStaff && (
              <Box>
                Are you sure you want to delete <strong>{deletingStaff.name}</strong>?
              </Box>
            )}
          </ModalBody>
          <ModalFooter justifyContent="center">
            <Button colorScheme="red" mx={3} onClick={confirmDelete}>
              Delete
            </Button>
            <Button variant="ghost" mx={3} onClick={() => { onCloseDelete(); setDeletingStaff(null); }}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Staff;
